import * as THREE from 'three';
import { PLAYER } from '../constants';
import { PlayerController } from './PlayerController';
import { Flashlight } from './Flashlight';
import { Weapon } from './Weapon';
import { InputManager } from '../input/InputManager';
import { EventEmitter } from '../core/EventEmitter';

export class Player {
  hp: number;
  maxHp: number;
  armor = 0;
  maxArmor = 50;
  coins: number = 0;
  controller: PlayerController;
  flashlight: Flashlight;
  events: EventEmitter;

  weapons: (Weapon | null)[] = [null, null, null]; // 3 slots
  activeWeapon: Weapon | null = null;
  activeSlot = 0;

  private lastDamageTime = 0;
  private regenDelay: number;
  private regenRate: number;
  private gameTime = 0;

  constructor(
    camera: THREE.PerspectiveCamera,
    input: InputManager,
    scene: THREE.Scene,
    events: EventEmitter,
    maxHp: number = PLAYER.DEFAULT_HP,
    regenRate: number = PLAYER.REGEN_RATE
  ) {
    this.maxHp = maxHp;
    this.hp = maxHp;
    this.regenDelay = PLAYER.REGEN_DELAY;
    this.regenRate = regenRate;
    this.events = events;

    this.controller = new PlayerController(camera, input);
    this.flashlight = new Flashlight(scene);
  }

  setWeapons(w1: Weapon | null, w2: Weapon | null, w3: Weapon | null = null): void {
    this.weapons = [w1, w2, w3];
    this.activeSlot = 0;
    this.activeWeapon = w1;
  }

  switchToSlot(slot: number): void {
    if (slot < 0 || slot >= 3) return;
    if (!this.weapons[slot]) return;
    this.activeSlot = slot;
    this.activeWeapon = this.weapons[slot];
    this.events.emit('weapon:switch', { weapon: this.activeWeapon });
  }

  switchWeapon(): void {
    // Cycle to next available weapon
    for (let i = 1; i <= 3; i++) {
      const next = (this.activeSlot + i) % 3;
      if (this.weapons[next]) {
        this.switchToSlot(next);
        return;
      }
    }
  }

  takeDamage(amount: number): void {
    let remaining = amount;
    // Armor absorbs damage first
    if (this.armor > 0) {
      const absorbed = Math.min(this.armor, remaining);
      this.armor -= absorbed;
      remaining -= absorbed;
    }
    this.hp = Math.max(0, this.hp - remaining);
    this.lastDamageTime = this.gameTime;
    this.events.emit('player:damaged', { amount, hp: this.hp, armor: this.armor });

    if (this.hp <= 0) {
      this.events.emit('player:dead');
    }
  }

  addArmor(amount: number): void {
    this.armor = Math.min(this.maxArmor, this.armor + amount);
  }

  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }

  isDead(): boolean {
    return this.hp <= 0;
  }

  update(dt: number, camera: THREE.PerspectiveCamera): void {
    this.gameTime += dt;

    // Controller update
    this.controller.update(dt);

    // Flashlight update
    this.flashlight.update(camera);

    // Weapon update — pass current state before update
    if (this.activeWeapon) {
      this.activeWeapon.isSprinting = this.controller.isSprinting;
      this.activeWeapon.isADS = this.controller.isADS;
      this.activeWeapon.update(dt);
    }

    // Auto HP regen
    if (this.hp < this.maxHp && this.hp > 0) {
      if (this.gameTime - this.lastDamageTime >= this.regenDelay) {
        this.hp = Math.min(this.maxHp, this.hp + this.regenRate * dt);
      }
    }
  }

  reset(): void {
    this.hp = this.maxHp;
    this.armor = 0;
    this.lastDamageTime = 0;
    this.gameTime = 0;
  }
}
